import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Plus, X, ImagePlus } from 'lucide-react';
import { CompanySelector } from '../shared/CompanySelector';
import { chatTheme as th } from '../../theme/chat';

export function DataExplorerInput({
  onSend, loading, models, selectedModel, onModelChange,
  selectedCompanies, onSelectCompany, onRemoveCompany
}) {
  const [message, setMessage] = useState('');
  const [images, setImages] = useState([]);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const isVisionModel = (selectedModel || '').toLowerCase().includes('gemini');

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed || loading) return;
    onSend(trimmed, images.length > 0 ? images : undefined);
    setMessage('');
    setImages([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const addImage = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024 || images.length >= 5) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setImages(prev => [...prev, { base64: dataUrl.split(',')[1], mime_type: file.type, preview: dataUrl }]);
    };
    reader.readAsDataURL(file);
  };

  const handlePaste = (e) => {
    if (!isVisionModel) return;
    for (const item of (e.clipboardData?.items || [])) {
      if (item.type.startsWith('image/')) { e.preventDefault(); addImage(item.getAsFile()); return; }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!isVisionModel) return;
    for (const file of e.dataTransfer.files) { if (file.type.startsWith('image/')) addImage(file); }
  };

  useEffect(() => { textareaRef.current?.focus(); }, []);

  useEffect(() => {
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 150) + 'px'; }
  }, [message]);

  return (
    <div className="p-3">
      <div className="max-w-3xl mx-auto">
        <div
          className={th.input.wrapper}
          onDragOver={isVisionModel ? (e) => e.preventDefault() : undefined}
          onDrop={isVisionModel ? handleDrop : undefined}
        >
          {/* Image previews */}
          {images.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {images.map((img, idx) => (
                <div key={idx} className="relative group">
                  <img src={img.preview} alt="attached" className={`h-16 rounded-md border object-cover ${th.imageBorder}`} />
                  <button onClick={() => setImages(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="h-2.5 w-2.5 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={isVisionModel ? "Ask a question or paste an image..." : "How can I help you today?"}
            rows={1}
            className={`w-full bg-transparent text-sm resize-none focus:outline-none ${th.input.text} ${th.input.placeholder}`}
            style={{ minHeight: '24px', maxHeight: '150px' }}
            disabled={loading}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1">
              {isVisionModel && (
                <>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple className="hidden"
                    onChange={(e) => { for (const f of e.target.files) addImage(f); e.target.value = ''; }} />
                  <button onClick={() => fileInputRef.current?.click()} disabled={loading || images.length >= 5}
                    className={`${th.input.iconColor} ${th.input.iconHover} disabled:text-zinc-300 transition-colors`}>
                    <ImagePlus className="h-4 w-4" />
                  </button>
                </>
              )}
              <button className={`${th.input.iconColor} ${th.input.iconHover} transition-colors`}>
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <CompanySelector
                selected={selectedCompanies || []}
                onSelect={onSelectCompany}
                onRemove={onRemoveCompany}
                showSync
                theme="light"
                trigger="button"
                direction="up"
              />

              <span className="w-px h-4 bg-zinc-200" />

              <select value={selectedModel} onChange={e => onModelChange(e.target.value)}
                className={th.modelSelector}>
                {(models || []).map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>

              <button onClick={handleSend} disabled={!message.trim() || loading}
                className={`${loading || !message.trim() ? th.sendBtn.disabled : th.sendBtn.active} transition-colors`}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
